import { GenericService } from 'services/genericService';
import {
	getCorpUserNumbersWithChilds,
	getBillingAccountInformation,
	getCorpAccountAdmins,
	getCorpAccountDetails,
	getCorpAccountManagers,
	getCorpAccountPhoto,
	getCorpAccountsForCurrentUser,
	getCorpAccountsWithAdminsForCurrentUser,
	getCustomerAccountOperation,
	getManagersForAccounts,
	getNumbersForAccount,
	getNumbersForBillingAccount,
	getStatisticsForAccounts,
	setCorpAccountPhoto,
	updateAccountNameForLoggedUser,
	updateBillingAccountInformation,
	updateCorpAccountContact,
	updateCorpAddress,
	addAllAccountsForArborIdInitialize,
	addAllAccountsForArborIdOperation,
	createHbbAccount,
	validateAdslLogin,
	updateOrganizationTaxId,
} from 'utils/api/queries/omantelApi';

import { Observable } from 'rxjs';
import * as payloads from 'utils/api/payloads/omantelApi';
import * as omantelMicroApi from 'utils/api/payloads/omantelMicroApi';
import { CorpAccounts } from 'utils/api/models/accounts/corpAccounts';
import { Accounts } from 'utils/api/models/accounts/accounts';
import { AccountDetails } from 'utils/api/models/accounts/accountDetails';
import { NumbersForAccount } from 'utils/api/models/accounts/numbersForAccount';
import { StatisticsForAccounts } from 'utils/api/models/accounts/statisticsForAccounts';
import { IBillingAccountInformation } from 'utils/api/models/accounts/billingAccountInformation';
import { CustomerAccountResponse } from 'utils/api/models/accounts/customerAccountResponse';
import { AvailableAccountManagers } from 'utils/api/models/accounts/availableAccountManagers';
import { Admins } from 'utils/api/models/accounts/admins';
import { Managers } from 'utils/api/models/accounts/managers';
import { NumbersForBillingAccount } from 'utils/api/models/accounts/numbersForBillingAccount';
import { UpdateOrganizationTaxId } from 'utils/api/models/accounts/updateOrganizationTaxId';
import { createFbbOrder, getBoosterPlans, getFbbOrderSummary } from 'utils/api/queries/omantelMicroApi';
import { validateUserName } from 'utils/api/queries/omantelMicroApi';
export class AccountsService extends GenericService {
	public static getCorpUserNumbersWithChilds(): Observable<Accounts> {
		return getCorpUserNumbersWithChilds()
			.withErrorHandling()
			.setTimeout(120000)
			.call();
	}

	public static getNumbersForBillingAccount(
		accountId: string,
		billingId: string
	): Observable<NumbersForBillingAccount> {
		return getNumbersForBillingAccount(accountId, billingId)
			.withErrorHandling()
			.call();
	}

	public static getCorpAccountsForCurrentUser(): Observable<CorpAccounts> {
		return getCorpAccountsForCurrentUser()
			.withErrorHandling()
			.call();
	}

	public static getCorpAccountsWithAdminsForCurrentUser(): Observable<CorpAccounts> {
		return getCorpAccountsWithAdminsForCurrentUser()
			.withErrorHandling()
			.call();
	}

	public static updateCorpAddress(accountDetails: payloads.AccountDetails): Observable<any> {
		return updateCorpAddress(accountDetails)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	public static getCustomerAccountOperation(accountInfo: payloads.AccountInfo): Observable<CustomerAccountResponse> {
		return getCustomerAccountOperation(accountInfo)
			.withErrorHandling()
			.call();
	}

	public static updateAccountNameForLoggedUser(accountData: payloads.AccountData): Observable<any> {
		return updateAccountNameForLoggedUser(accountData).call();
	}

	public static getCorpAccountDetails(accountId: string): Observable<AccountDetails> {
		return getCorpAccountDetails(accountId)
			.withErrorHandling()
			.call();
	}

	public static getCorpAccountPhoto(accountId: string): Observable<any> {
		return getCorpAccountPhoto(accountId).call();
	}

	public static setCorpAccountPhoto(photoData: payloads.PhotoData): Observable<any> {
		return setCorpAccountPhoto(photoData)
			.disableCache()
			.withErrorHandling()
			.call();
	}

	public static getNumbersForAccount(accountId: string, unassignedOnly?: boolean): Observable<NumbersForAccount> {
		return getNumbersForAccount(accountId, unassignedOnly).call();
	}

	public static getCorpAccountAdmins(accountId: string): Observable<Admins> {
		return getCorpAccountAdmins(accountId)
			.withErrorHandling()
			.call();
	}

	public static getCorpAccountManagers(accountId: string): Observable<Managers> {
		return getCorpAccountManagers(accountId)
			.withErrorHandling()
			.call();
	}

	public static updateCorpAccountContact(accountContactData: payloads.AccountContactData): Observable<any> {
		return updateCorpAccountContact(accountContactData)
			.disableCache()
			.withErrorHandling()
			.call();
	}

	public static updateOrganizationTaxId(
		taxData: payloads.UpdateOrganizationTaxId
	): Observable<UpdateOrganizationTaxId> {
		return updateOrganizationTaxId(taxData)
			.disableCache()
			.withErrorHandling()
			.call();
	}

	public static getManagersForAccounts(
		departmentId: string,
		accountId: string
	): Observable<AvailableAccountManagers> {
		return getManagersForAccounts(departmentId, accountId).call();
	}

	public static getStatisticsForAccounts(): Observable<StatisticsForAccounts> {
		return getStatisticsForAccounts()
			.withErrorHandling()
			.call();
	}

	public static getBillingAccountInformation({
		accountId,
		billingId,
	}: {
		accountId: string;
		billingId: string;
	}): Observable<IBillingAccountInformation> {
		return getBillingAccountInformation({ accountId, billingId })
			.withErrorHandling()
			.call();
	}

	public static updateBillingAccountInformation(data: payloads.UpdateBillingAccountInformationData): Observable<any> {
		return updateBillingAccountInformation(data)
			.withErrorHandling()
			.call();
	}

	public static addAllAccountsForArborIdInitialize(
		data: payloads.AddAllAccountsForArborIdInitializeData
	): Observable<any> {
		return addAllAccountsForArborIdInitialize(data)
			.disableCache()
			.call();
	}

	public static addAllAccountsForArborIdOperation(data: payloads.AddBillingAccountData): Observable<any> {
		return addAllAccountsForArborIdOperation(data)
			.disableCache()
			.call();
	}

	public static createHbbAccount(data: payloads.CreateHbbAccountData): Observable<any> {
		return createHbbAccount(data).call();
	}
	public static createFbbOrder(data: omantelMicroApi.CreateFbbOrder, isFiberPro2b: boolean): Observable<any> {
		return createFbbOrder(data, isFiberPro2b)
			.disableCache()
			.call();
	}
	public static getFbbOrderSummary(data: omantelMicroApi.FbbOrderSummary): Observable<any> {
		return getFbbOrderSummary(data)
			.disableCache()
			.call();
	}
	public static getBoosterPlans(
		subscriberNumber: string,
		isCopperUser: boolean,
		productId?: string
	): Observable<any> {
		return getBoosterPlans(subscriberNumber, isCopperUser, productId)
			.disableCache()
			.call();
	}
	public static validateAdslLogin(adslLogin: string): Observable<any> {
		return validateAdslLogin(adslLogin).call();
	}
	public static validateUserName(adslLogin: string): Observable<any> {
		return validateUserName(adslLogin).call();
	}
}
